import { useState, useEffect, useRef, useCallback } from 'react'
import { NetworkRequestError } from '../services/lib'
import { formatError } from '../redux/lib/errors'

interface UseAsyncServiceOptions<A, T> {
  serviceCall: (args: A) => Promise<T>
  delay?: number
  initialArgs: A | null
  validateServiceResults?: (res: unknown) => boolean
}

interface UseAsyncServiceState<A, T> {
  serviceLoading: boolean
  serviceValue: T | null
  serviceError: string | null
  setServiceArgs: (args: A | null) => void
}

function useAsyncService<A, T>({
  serviceCall,
  delay = 500,
  initialArgs,
  validateServiceResults
}: UseAsyncServiceOptions<A, T>): UseAsyncServiceState<A, T> {
  const [serviceArgs, setServiceArgs] = useState(initialArgs)
  const [serviceLoading, setServiceLoading] = useState(false)
  const [serviceValue, setServiceValue] = useState<T | null>(null)
  const [serviceError, setServiceError] = useState<string | null>(null)

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const executeServiceCall = useCallback(
    async (args: A) => {
      try {
        const result = await serviceCall(args)
        if (validateServiceResults && !validateServiceResults(result)) {
          setServiceError('Response failed to validate')
        } else {
          setServiceValue(result)
        }
      } catch (err) {
        setServiceError(formatError(err).message || 'Unknown Error')
      } finally {
        setServiceLoading(false)
      }
    },
    [serviceCall]
  )

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (serviceArgs === null) {
      setServiceLoading(false)
      setServiceValue(null)
      setServiceError(null)
      return
    }

    setServiceLoading(true)
    setServiceValue(null)
    setServiceError(null)

    timeoutRef.current = setTimeout(() => {
      executeServiceCall(serviceArgs)
    }, delay)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [serviceArgs, executeServiceCall, delay])

  return { serviceLoading, serviceValue, serviceError, setServiceArgs }
}

export default useAsyncService
